import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { LAUNCH_APP_URL } from "@/config";

const NAV_LINKS = [
  { label: "Business Impact", id: "business-impact" },
  { label: "AI Features", id: "ai-features" },
  { label: "Testimonials", id: "testimonials" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setMenuOpen(false);
    scrollToSection(id);
  };

  return (
    <motion.header
      data-testid="landing-navbar"
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-white/70 backdrop-blur-xl border-b border-black/[0.06]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
        <button
          data-testid="navbar-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="group flex items-center gap-2.5"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0a0a0a] text-[13px] font-heading font-bold text-white">
            A
          </span>
          <span className="font-heading text-[15px] font-bold tracking-tight text-[#0a0a0a]">
            AMRA <span className="text-brand-teal">AI</span> Business OS
          </span>
        </button>

        <div className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-link-${link.id}`}
              onClick={() => handleNav(link.id)}
              className="relative rounded-full px-4 py-2 text-sm font-medium text-[#52525b] transition-colors duration-200 hover:text-[#0a0a0a]"
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <a
            data-testid="navbar-launch-app"
            href={LAUNCH_APP_URL}
            target="_blank"
            rel="noreferrer"
            className="group hidden items-center gap-1.5 rounded-full bg-[#0a0a0a] px-5 py-2.5 text-sm font-medium text-white transition-transform duration-200 hover:scale-[1.03] sm:inline-flex"
          >
            Launch App
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>

          <button
            data-testid="navbar-menu-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/[0.08] text-[#0a0a0a] md:hidden"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden border-b border-black/[0.06] bg-white/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  data-testid={`mobile-nav-link-${link.id}`}
                  onClick={() => handleNav(link.id)}
                  className="rounded-lg px-3 py-3 text-left text-[15px] font-medium text-[#0a0a0a] hover:bg-[#f4f4f5]"
                >
                  {link.label}
                </button>
              ))}
              <a
                data-testid="mobile-launch-app"
                href={LAUNCH_APP_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0a0a0a] px-5 py-3 text-sm font-medium text-white"
              >
                Launch App
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
